/**
 * MeasurementGuide — luxury AI tailoring operating system view.
 *
 * Schema-driven (zero garment-hardcoded logic) so adding kurta/sherwani/etc
 * needs only schema entries.
 *
 * Refinements (Iter 41):
 *  · Editorial blueprint diagram (thin gold lines, ivory bg, refined labels)
 *  · Tap-any-measurement help cards (tape, direction, mistakes, fit impact)
 *  · Per-measurement AI confidence chips (computed from source + field type)
 *  · Sticky source legend
 *  · Tailor recommendation engine (rule-based, additive)
 *  · Critical-missing measurement warnings
 *  · Beginner mode toggle (sewing notes)
 *  · Mobile-first layout
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown, ChevronUp, Check, MessageCircleQuestion,
  ScrollText, Info, X, AlertTriangle, Sparkles, BookOpen, Zap,
} from 'lucide-react';
import {
  buildTailorView, schemaKeyForCategory, GROUP_ORDER, GROUP_META,
} from '../../data/tailorMeasurementGuide';
import {
  FIELD_HELP, GENERIC_HELP, BEGINNER_NOTES,
  computeConfidence, findMissingCritical, buildRecommendations,
} from '../../data/tailorMeasurementHelp';

const GOLD = '#D4AF37';
const GOLD_SOFT = '#B8A36F';
const INK = '#1A1306';
const MUTED = '#7A6A4D';
const LINE = '#EAE2D0';
const SOFT = '#FBF8F0';
const SUCCESS = '#166534';
const WARN = '#9A6A0F';
const DANGER = '#991B1B';

// Source chips — luxury terminology refresh. The "pending" tone is now a soft
// gold/amber warn (not an aggressive red); the truly-blocking red is reserved
// for the dedicated "Manual Confirmation Needed" panel below.
const SOURCE_CHIP = {
  scan_ease: { label: 'Scan + Ease',        bg: 'rgba(22,101,52,0.10)',  fg: SUCCESS },
  scan:     { label: 'From Scan',           bg: 'rgba(22,101,52,0.10)',  fg: SUCCESS },
  edited:   { label: 'Customer Edited',     bg: 'rgba(30,64,175,0.08)',  fg: '#1E40AF' },
  choice:   { label: 'Customer Choice',     bg: 'rgba(30,64,175,0.08)',  fg: '#1E40AF' },
  rule:     { label: 'Fit Rule',            bg: 'rgba(212,175,55,0.10)', fg: '#7A6420' },
  recorded: { label: 'Recorded · Source Unknown', bg: 'rgba(89,89,89,0.08)', fg: '#595959' },
  derived:  { label: 'AI Estimated',        bg: 'rgba(212,175,55,0.16)', fg: WARN },
  default:  { label: 'Recommended Default', bg: 'rgba(212,175,55,0.10)', fg: '#7A6420' },
  pending:  { label: 'Needs Verification',  bg: 'rgba(154,106,15,0.12)', fg: WARN },
};

const TONE_STYLE = {
  warn: { bg: 'rgba(153,27,27,0.06)', fg: DANGER, border: 'rgba(153,27,27,0.32)', Icon: AlertTriangle },
  info: { bg: 'rgba(212,175,55,0.08)', fg: WARN, border: 'rgba(212,175,55,0.40)', Icon: Sparkles },
  tip:  { bg: 'rgba(22,101,52,0.06)', fg: SUCCESS, border: 'rgba(22,101,52,0.30)', Icon: Info },
};

// ─── Editorial blueprint diagram (refined) ──────────────────────────────────
const Diagram = ({ schemaKey }) => {
  // Refined editorial style:
  //  · ivory linen background
  //  · 0.8px gold lines (was 1.4)
  //  · serif label font, hairline lead lines (0.4px dotted)
  //  · larger spacing between labels and body
  const pageBg = '#FCF7E8';
  const bodyStroke = '#C9A95A';
  const bodyFill = 'rgba(255, 250, 235, 0.85)';
  const leadStroke = 'rgba(184, 163, 111, 0.7)';
  const labelFill = '#3A2F1A';

  const dot = (cx, cy, key) => (
    <circle key={key} cx={cx} cy={cy} r="2" fill={GOLD} stroke="#FFF" strokeWidth="0.6" />
  );
  const lead = (x1, y1, x2, y2, key) => (
    <line key={key} x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={leadStroke} strokeWidth="0.4" strokeDasharray="1.6 1.6" />
  );
  const label = (x, y, text, anchor = 'start', key) => (
    <text key={key} x={x} y={y} fontSize="8.2" fontFamily="Playfair Display, serif"
      fontStyle="italic" fontWeight="500"
      fill={labelFill} textAnchor={anchor}
      style={{ letterSpacing: '0.02em' }}>{text}</text>
  );
  const stroke = { fill: 'none', stroke: bodyStroke, strokeWidth: 0.8,
                   strokeLinecap: 'round', strokeLinejoin: 'round' };

  if (schemaKey === 'blouse' || schemaKey === 'suit') {
    return (
      <svg viewBox="0 0 360 360" width="100%" height="100%" style={{ display: 'block' }}>
        <rect width="360" height="360" fill={pageBg} />
        {/* Subtle blueprint grid */}
        <g opacity="0.16">
          {[...Array(12)].map((_, i) =>
            <line key={`h${i}`} x1="0" y1={i * 30} x2="360" y2={i * 30} stroke={GOLD_SOFT} strokeWidth="0.3" />)}
          {[...Array(12)].map((_, i) =>
            <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2="360" stroke={GOLD_SOFT} strokeWidth="0.3" />)}
        </g>
        {/* Body silhouette */}
        <path d="M 180 70
                 Q 152 70 150 88
                 L 112 120 Q 120 144 130 140
                 L 138 220 Q 150 240 180 242
                 Q 210 240 222 220 L 230 140
                 Q 240 144 248 120 L 210 88
                 Q 208 70 180 70 Z" {...stroke} fill={bodyFill} />
        {/* Neckline V */}
        <path d="M 165 80 Q 180 102 195 80" {...stroke} />
        {/* Bust contour */}
        <path d="M 150 132 Q 180 146 210 132" {...stroke} strokeOpacity="0.55" />
        {/* Waist contour */}
        <path d="M 144 208 Q 180 216 216 208" {...stroke} strokeOpacity="0.45" />

        {/* Labels — 2 column layout (left & right) */}
        {[
          [180, 76,  340, 60,  'Neck Width',        'end',   'a'],
          [180, 100, 20,  88,  'Front Neck Depth',  'start', 'b'],
          [115, 100, 20,  108, 'Shoulder',          'start', 'c'],
          [130, 140, 20,  150, 'Armhole',           'start', 'd'],
          [180, 105, 340, 100, 'Upper Bust',        'end',   'e'],
          [180, 132, 340, 138, 'Bust',              'end',   'f'],
          [180, 148, 20,  178, 'Under Bust',        'start', 'g'],
          [180, 215, 340, 215, 'Waist',             'end',   'h'],
          [160, 132, 20,  208, 'Apex Distance',     'start', 'i'],
          [225, 178, 340, 188, 'Side Length',       'end',   'j'],
          [180, 242, 340, 248, 'Blouse Length',     'end',   'k'],
        ].map(([cx, cy, lx, ly, text, anchor, k]) => (
          <g key={k}>
            {dot(cx, cy, `d-${k}`)}
            {lead(cx, cy, anchor === 'end' ? lx - 4 : lx + 4, ly - 3, `l-${k}`)}
            {label(lx, ly, text, anchor, `t-${k}`)}
          </g>
        ))}
      </svg>
    );
  }

  if (schemaKey === 'kurta_pajama' || schemaKey === 'sherwani') {
    return (
      <svg viewBox="0 0 360 400" width="100%" height="100%" style={{ display: 'block' }}>
        <rect width="360" height="400" fill={pageBg} />
        <g opacity="0.16">
          {[...Array(13)].map((_, i) =>
            <line key={`h${i}`} x1="0" y1={i * 30} x2="360" y2={i * 30} stroke={GOLD_SOFT} strokeWidth="0.3" />)}
          {[...Array(12)].map((_, i) =>
            <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2="400" stroke={GOLD_SOFT} strokeWidth="0.3" />)}
        </g>
        <path d="M 180 60
                 Q 148 60 144 80
                 L 100 110 Q 108 134 120 132
                 L 130 270 Q 150 276 180 276
                 Q 210 276 230 270 L 240 132
                 Q 252 134 260 110 L 216 80
                 Q 212 60 180 60 Z" {...stroke} fill={bodyFill} />
        <path d="M 162 66 Q 180 76 198 66 L 198 60 Q 180 56 162 60 Z" {...stroke} />
        <line x1="180" y1="76" x2="180" y2="190" stroke={bodyStroke} strokeWidth="0.6" />
        <line x1="135" y1="234" x2="135" y2="276" stroke={bodyStroke} strokeWidth="0.6" />
        <line x1="225" y1="234" x2="225" y2="276" stroke={bodyStroke} strokeWidth="0.6" />

        {[
          [180, 70,  340, 64,  'Neck',           'end',   'a'],
          [105, 96,  20,  90,  'Shoulder',       'start', 'b'],
          [120, 134, 20,  138, 'Armhole',        'start', 'c'],
          [180, 142, 340, 144, 'Chest',          'end',   'd'],
          [180, 210, 340, 210, 'Waist',          'end',   'e'],
          [180, 276, 340, 282,
            schemaKey === 'sherwani' ? 'Sherwani Length' : 'Kurta Length', 'end', 'f'],
          [135, 234, 20,  238, 'Side Slit',      'start', 'g'],
          [180, 188, 340, 188, 'Placket Length', 'end',   'h'],
        ].map(([cx, cy, lx, ly, text, anchor, k]) => (
          <g key={k}>
            {dot(cx, cy, `d-${k}`)}
            {lead(cx, cy, anchor === 'end' ? lx - 4 : lx + 4, ly - 3, `l-${k}`)}
            {label(lx, ly, text, anchor, `t-${k}`)}
          </g>
        ))}
      </svg>
    );
  }

  if (schemaKey === 'men_shirt') {
    return (
      <svg viewBox="0 0 360 360" width="100%" height="100%" style={{ display: 'block' }}>
        <rect width="360" height="360" fill={pageBg} />
        <g opacity="0.16">
          {[...Array(12)].map((_, i) =>
            <line key={`h${i}`} x1="0" y1={i * 30} x2="360" y2={i * 30} stroke={GOLD_SOFT} strokeWidth="0.3" />)}
          {[...Array(12)].map((_, i) =>
            <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2="360" stroke={GOLD_SOFT} strokeWidth="0.3" />)}
        </g>
        <path d="M 180 60
                 Q 150 60 146 78
                 L 106 110 Q 114 134 126 132
                 L 136 248 Q 158 258 180 258
                 Q 202 258 224 248 L 234 132
                 Q 246 134 254 110 L 214 78
                 Q 210 60 180 60 Z" {...stroke} fill={bodyFill} />
        <path d="M 158 66 L 180 86 L 202 66" {...stroke} />
        <line x1="180" y1="86" x2="180" y2="220" stroke={bodyStroke} strokeWidth="0.6" />

        {[
          [180, 70,  340, 64,  'Neck',          'end',   'a'],
          [111, 96,  20,  90,  'Shoulder',      'start', 'b'],
          [126, 132, 20,  138, 'Armhole',       'start', 'c'],
          [180, 142, 340, 144, 'Chest',         'end',   'd'],
          [180, 200, 340, 200, 'Waist',         'end',   'e'],
          [180, 258, 340, 264, 'Shirt Length',  'end',   'f'],
          [158, 74,  20,  60,  'Collar Height', 'start', 'g'],
        ].map(([cx, cy, lx, ly, text, anchor, k]) => (
          <g key={k}>
            {dot(cx, cy, `d-${k}`)}
            {lead(cx, cy, anchor === 'end' ? lx - 4 : lx + 4, ly - 3, `l-${k}`)}
            {label(lx, ly, text, anchor, `t-${k}`)}
          </g>
        ))}
      </svg>
    );
  }

  if (schemaKey === 'trouser') {
    return (
      <svg viewBox="0 0 360 400" width="100%" height="100%" style={{ display: 'block' }}>
        <rect width="360" height="400" fill={pageBg} />
        <g opacity="0.16">
          {[...Array(13)].map((_, i) =>
            <line key={`h${i}`} x1="0" y1={i * 30} x2="360" y2={i * 30} stroke={GOLD_SOFT} strokeWidth="0.3" />)}
          {[...Array(12)].map((_, i) =>
            <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2="400" stroke={GOLD_SOFT} strokeWidth="0.3" />)}
        </g>
        <path d="M 150 60 L 150 80 L 130 360 L 170 360 L 176 150 L 184 150 L 190 360 L 230 360 L 210 80 L 210 60 Z"
          {...stroke} fill={bodyFill} />

        {[
          [180, 66,  340, 60,  'Waist',     'end',   'a'],
          [180, 90,  340, 96,  'Hip',       'end',   'b'],
          [160, 150, 20,  150, 'Thigh',     'start', 'c'],
          [166, 250, 20,  250, 'Knee',      'start', 'd'],
          [180, 78,  340, 116, 'Rise',      'end',   'e'],
          [170, 360, 20,  360, 'Bottom Hem','start', 'f'],
          [160, 220, 20,  220, 'Inseam',    'start', 'g'],
        ].map(([cx, cy, lx, ly, text, anchor, k]) => (
          <g key={k}>
            {dot(cx, cy, `d-${k}`)}
            {lead(cx, cy, anchor === 'end' ? lx - 4 : lx + 4, ly - 3, `l-${k}`)}
            {label(lx, ly, text, anchor, `t-${k}`)}
          </g>
        ))}
      </svg>
    );
  }

  return (
    <div className="flex items-center justify-center w-full h-full" style={{ background: pageBg }}>
      <p className="text-[11px]" style={{ color: MUTED, fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>
        Diagram unavailable for this category.
      </p>
    </div>
  );
};

// ─── Help card modal (tap any measurement to open) ─────────────────────────
const HelpCard = ({ field, beginner, onClose }) => {
  const help = FIELD_HELP[field.id] || GENERIC_HELP;
  const beginnerNote = beginner ? BEGINNER_NOTES[field.id] : null;
  return (
    <div data-testid="help-card" className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} style={{ backdropFilter: 'blur(6px)' }} />
      <div className="relative w-full sm:max-w-sm sm:mx-4 bg-white shadow-2xl animate-modalUp"
        style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: LINE }}>
          <div>
            <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: GOLD }}>How to Measure</p>
            <h3 className="text-[16px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
              {field.label}
            </h3>
          </div>
          <button data-testid="help-close" onClick={onClose} className="p-1">
            <X className="w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />
          </button>
        </div>
        <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Tape position */}
          <div>
            <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: GOLD, fontWeight: 600 }}>Tape Position</p>
            <p className="text-[13px]" style={{ color: INK, lineHeight: 1.5 }}>{help.tape}</p>
          </div>
          {/* Direction */}
          <div>
            <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: GOLD, fontWeight: 600 }}>Direction</p>
            <p className="text-[13px]" style={{ color: INK, lineHeight: 1.5 }}>{help.direction}</p>
          </div>
          {/* Common mistakes */}
          <div>
            <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: DANGER, fontWeight: 600 }}>Common Mistakes</p>
            <ul className="space-y-1">
              {(help.mistakes || []).map((m, i) => (
                <li key={i} className="text-[12px] flex items-start gap-2" style={{ color: INK, lineHeight: 1.5 }}>
                  <span style={{ color: DANGER }}>•</span> {m}
                </li>
              ))}
            </ul>
          </div>
          {/* Fit impact */}
          <div>
            <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: WARN, fontWeight: 600 }}>Fit Impact</p>
            <p className="text-[12px]" style={{ color: INK, lineHeight: 1.5 }}>{help.impact}</p>
          </div>
          {/* Beginner-mode sewing note */}
          {beginnerNote && (
            <div className="p-2.5" style={{ background: 'rgba(212,175,55,0.10)', border: `1px solid ${GOLD}`}}>
              <p className="text-[9px] tracking-[0.18em] uppercase mb-1 flex items-center gap-1.5" style={{ color: GOLD, fontWeight: 600 }}>
                <BookOpen className="w-3 h-3" strokeWidth={1.6} /> Beginner Sewing Note
              </p>
              <p className="text-[12px]" style={{ color: INK, lineHeight: 1.5 }}>{beginnerNote}</p>
            </div>
          )}
        </div>
        <button onClick={onClose} className="w-full py-3 text-[10px] tracking-[0.18em] uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #1A1306 0%, #D4AF37 140%)', fontWeight: 500 }}>
          Got It
        </button>
      </div>
    </div>
  );
};

// ─── Confidence chip ────────────────────────────────────────────────────────
const ConfidenceChip = ({ value }) => {
  let color, bg;
  if (value >= 88)      { color = SUCCESS; bg = 'rgba(22,101,52,0.10)'; }
  else if (value >= 65) { color = WARN;    bg = 'rgba(212,175,55,0.14)'; }
  else if (value > 0)   { color = DANGER;  bg = 'rgba(153,27,27,0.10)'; }
  else                  { color = MUTED;   bg = 'rgba(120,120,120,0.10)'; }
  return (
    <span className="text-[8.5px] tracking-[0.04em] px-1.5 py-0.5"
      style={{ background: bg, color, fontWeight: 700, letterSpacing: '0.03em' }}>
      {value > 0 ? `${value}% AI` : '— AI'}
    </span>
  );
};

// ─── Field row ──────────────────────────────────────────────────────────────
const FieldRow = ({ field, verified, confidence, expertMode, onToggleVerified, onOpenHelp }) => {
  const src = SOURCE_CHIP[field.source] || SOURCE_CHIP.pending;
  const display = field.value == null
    ? '—'
    : `${field.value}${field.unit ? ` ${field.unit}` : ''}`;

  // EXPERT MODE — single dense row, value on the right, no hints, tighter padding.
  if (expertMode) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b last:border-b-0"
        data-testid={`tg-field-${field.id}`}
        style={{ borderColor: '#F0EBE2', background: verified ? 'rgba(22,101,52,0.04)' : '#FFF' }}>
        <button
          data-testid={`tg-help-${field.id}`} onClick={onOpenHelp}
          className="flex-1 flex items-center gap-1.5 text-left active:opacity-70 min-w-0">
          <p className="text-[11.5px] truncate" style={{ color: INK, fontWeight: 500 }}>{field.label}</p>
          {field.required && (
            <span className="text-[7.5px] uppercase tracking-[0.10em]"
              style={{ color: GOLD, fontWeight: 700 }}>·R</span>
          )}
        </button>
        <p className="text-[13px] tabular-nums"
          style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500, minWidth: 56, textAlign: 'right' }}>
          {display}
        </p>
        <ConfidenceChip value={confidence} />
        <span className="text-[8px] tracking-[0.06em] uppercase whitespace-nowrap px-1 py-0.5"
          style={{ background: src.bg, color: src.fg, fontWeight: 600 }}>
          {src.label[0]}
        </span>
        <button
          data-testid={`tg-verify-${field.id}`} onClick={onToggleVerified} aria-pressed={verified}
          className="w-6 h-6 flex items-center justify-center transition active:scale-90"
          style={{
            background: verified ? SUCCESS : '#FFF',
            color: verified ? '#FFF' : MUTED,
            border: `1px solid ${verified ? SUCCESS : LINE}`,
          }}>
          <Check className="w-3 h-3" strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2.5 border-b last:border-b-0"
      data-testid={`tg-field-${field.id}`}
      style={{ borderColor: '#F0EBE2', background: verified ? 'rgba(22,101,52,0.04)' : '#FFF' }}>
      {/* Label row */}
      <div className="flex items-start justify-between gap-2 mb-0.5">
        <button
          data-testid={`tg-help-${field.id}`} onClick={onOpenHelp}
          className="flex items-center gap-1.5 min-w-0 group active:opacity-70 transition">
          <p className="text-[12px] font-medium text-left" style={{ color: INK }}>{field.label}</p>
          <Info className="w-2.5 h-2.5 opacity-50 group-hover:opacity-100 transition" style={{ color: GOLD }} strokeWidth={1.8} />
          {field.required && (
            <span className="text-[8px] uppercase tracking-[0.12em]"
              style={{ color: GOLD, fontWeight: 600 }}>Req</span>
          )}
        </button>
        <div className="flex items-center gap-1">
          <ConfidenceChip value={confidence} />
          <span className="text-[8.5px] tracking-[0.10em] uppercase whitespace-nowrap px-1.5 py-0.5"
            style={{ background: src.bg, color: src.fg, fontWeight: 600 }}>
            {src.label}
          </span>
        </div>
      </div>
      {field.hint && (
        <p className="text-[10px] mb-1.5" style={{ color: MUTED, lineHeight: 1.4 }}>
          {field.hint}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[15px]"
          style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
          {display}
        </p>
        <button
          data-testid={`tg-verify-${field.id}`} onClick={onToggleVerified}
          aria-pressed={verified}
          className="flex items-center gap-1.5 px-2.5 py-1 transition active:scale-95"
          style={{
            background: verified ? SUCCESS : '#FFF',
            color: verified ? '#FFF' : MUTED,
            border: `1px solid ${verified ? SUCCESS : LINE}`,
            fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
            fontWeight: 600,
          }}>
          <Check className="w-2.5 h-2.5" strokeWidth={2.5} />
          {verified ? 'Verified' : 'Verify'}
        </button>
      </div>
    </div>
  );
};

// ─── Source legend (sticky banner) ─────────────────────────────────────────
const SourceLegend = () => (
  <div data-testid="tg-source-legend" className="px-3 py-2 border-b flex items-center gap-2 overflow-x-auto"
    style={{ borderColor: LINE, background: '#FFF' }}>
    <p className="text-[9px] tracking-[0.18em] uppercase flex-shrink-0" style={{ color: MUTED, fontWeight: 600 }}>Legend</p>
    {[
      { lbl: 'From Scan / Scan + Ease', bg: 'rgba(22,101,52,0.10)',  fg: SUCCESS },
      { lbl: 'Customer Edited / Choice', bg: 'rgba(30,64,175,0.08)', fg: '#1E40AF' },
      { lbl: 'Fit Rule',             bg: 'rgba(212,175,55,0.10)', fg: '#7A6420' },
      { lbl: 'AI Estimated',         bg: 'rgba(212,175,55,0.16)', fg: WARN },
      { lbl: 'Recommended Default',  bg: 'rgba(212,175,55,0.10)', fg: '#7A6420' },
      { lbl: 'Tailor Verified',      bg: SUCCESS, fg: '#FFF' },
      { lbl: 'Needs Verification',   bg: 'rgba(154,106,15,0.12)', fg: WARN },
    ].map((it, i) => (
      <span key={i} className="text-[9px] tracking-[0.06em] px-1.5 py-0.5 flex-shrink-0"
        style={{ background: it.bg, color: it.fg, fontWeight: 600, letterSpacing: '0.02em' }}>
        {it.lbl}
      </span>
    ))}
  </div>
);

// ─── Recommendation card ───────────────────────────────────────────────────
const RecommendationCard = ({ rec }) => {
  const t = TONE_STYLE[rec.tone] || TONE_STYLE.tip;
  const Icon = t.Icon;
  return (
    <div className="px-3 py-2 mb-1.5" style={{ background: t.bg, border: `1px solid ${t.border}` }}>
      <div className="flex items-start gap-2">
        <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: t.fg }} strokeWidth={1.6} />
        <div>
          <p className="text-[11px] mb-0.5" style={{ color: t.fg, fontWeight: 600 }}>{rec.title}</p>
          <p className="text-[11px]" style={{ color: INK, lineHeight: 1.45 }}>{rec.body}</p>
        </div>
      </div>
    </div>
  );
};

// ─── Top-level component ────────────────────────────────────────────────────
const MeasurementGuide = ({
  orderId, category, measurements = {},
  provenance = null,
  customizations = {},
  fitConfidence,
  onRequestClarification,
}) => {
  const schemaKey = useMemo(() => schemaKeyForCategory(category), [category]);
  const fields = useMemo(() => buildTailorView(category, measurements, provenance), [category, measurements, provenance]);
  const fitPref = measurements?.fit;
  const recommendations = useMemo(
    () => buildRecommendations({ schemaKey, customizations, measurements, fitPref }),
    [schemaKey, customizations, measurements, fitPref]);
  const missing = useMemo(
    () => findMissingCritical(schemaKey, fields), [schemaKey, fields]);

  // localStorage state
  const stateKey = `lunora_tailor_${orderId || 'unknown'}`;
  const [verified, setVerified] = useState({});
  const [notes, setNotes] = useState('');
  const [beginnerMode, setBeginnerMode] = useState(false);
  const [expertMode, setExpertMode] = useState(false);
  const [helpField, setHelpField] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => {
    const out = {};
    GROUP_ORDER.forEach((g, i) => { out[g] = i < 3; });
    return out;
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(stateKey);
      if (raw) {
        const s = JSON.parse(raw);
        setVerified(s.verified || {});
        setNotes(s.notes || '');
        setBeginnerMode(!!s.beginnerMode);
        setExpertMode(!!s.expertMode);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const persist = (next) => {
    try { localStorage.setItem(stateKey, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const toggleVerified = (id) => {
    const next = { ...verified, [id]: !verified[id] };
    setVerified(next);
    persist({ verified: next, notes, beginnerMode, expertMode });
  };
  const updateNotes = (v) => {
    setNotes(v);
    persist({ verified, notes: v, beginnerMode, expertMode });
  };
  const toggleBeginner = () => {
    const v = !beginnerMode;
    setBeginnerMode(v);
    // Beginner and expert are mutually exclusive
    const exp = v ? false : expertMode;
    setExpertMode(exp);
    persist({ verified, notes, beginnerMode: v, expertMode: exp });
  };
  const toggleExpert = () => {
    const v = !expertMode;
    setExpertMode(v);
    const beg = v ? false : beginnerMode;
    setBeginnerMode(beg);
    // When entering expert mode, expand all groups for fast scanning
    if (v) {
      const all = {};
      GROUP_ORDER.forEach(g => { all[g] = true; });
      setOpenGroups(all);
    }
    persist({ verified, notes, beginnerMode: beg, expertMode: v });
  };

  // Group fields & compute confidence per field
  const grouped = useMemo(() => {
    const out = {};
    GROUP_ORDER.forEach(g => { out[g] = []; });
    fields.forEach(f => { (out[f.group] || (out[f.group] = [])).push(f); });
    return out;
  }, [fields]);
  const confidenceFor = useMemo(() => {
    const map = {};
    fields.forEach(f => { map[f.id] = computeConfidence(f); });
    return map;
  }, [fields]);

  const verifiedCount = Object.values(verified).filter(Boolean).length;
  const totalCount = fields.length;

  if (!schemaKey) {
    return (
      <div data-testid="measurement-guide-unsupported"
        className="p-3 border" style={{ borderColor: LINE, background: SOFT }}>
        <p className="text-[11px]" style={{ color: MUTED }}>
          Measurement guide unavailable for this garment category.
        </p>
      </div>
    );
  }

  return (
    <>
      <div data-testid="measurement-guide" className="bg-white"
        style={{
          border: `1px solid ${LINE}`,
          boxShadow: '0 12px 32px rgba(60,40,20,0.06), 0 1px 2px rgba(60,40,20,0.04)',
          borderRadius: 4,
          overflow: 'hidden',
        }}>
        {/* Header — refined typography hierarchy */}
        <div className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: LINE,
            background: 'linear-gradient(90deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 60%, transparent 100%)' }}>
          <div className="flex items-center gap-2">
            <ScrollText className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
            <p className="text-[9.5px] tracking-[0.24em] uppercase" style={{ color: GOLD, fontWeight: 700 }}>
              Tailoring Measurement Guide
            </p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px]" style={{ color: MUTED }}>
              <span style={{ color: SUCCESS, fontWeight: 700 }}>{verifiedCount}</span>
              <span> / {totalCount} verified</span>
            </p>
            {/* Expert toggle (compact dense workflow) */}
            <button
              data-testid="tg-expert-toggle" onClick={toggleExpert}
              aria-pressed={expertMode}
              className="flex items-center gap-1 px-1.5 py-0.5 transition active:scale-95"
              style={{
                background: expertMode ? INK : '#FFF',
                color: expertMode ? GOLD : MUTED,
                border: `1px solid ${expertMode ? INK : LINE}`,
                fontSize: 8.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                fontWeight: 700,
              }}>
              <Zap className="w-2.5 h-2.5" strokeWidth={2} />
              Expert
            </button>
          </div>
        </div>

        {/* Sticky source legend */}
        <SourceLegend />

        {/* Editorial blueprint diagram + summary — hidden in Expert Mode for density */}
        {!expertMode && (
        <div className="grid grid-cols-2 gap-0 border-b" style={{ borderColor: LINE }}>
          <div className="aspect-square overflow-hidden" data-testid="tg-diagram"
            style={{ borderRight: `1px solid ${LINE}` }}>
            <Diagram schemaKey={schemaKey} />
          </div>
          <div className="p-4 flex flex-col justify-center"
            style={{ background: 'linear-gradient(180deg, #FBF8F0 0%, #F5EFE0 100%)' }}>
            <p className="text-[9px] tracking-[0.24em] uppercase mb-1" style={{ color: MUTED, fontWeight: 600 }}>Garment</p>
            <p className="text-[16px] mb-3"
              style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500, textTransform: 'capitalize', letterSpacing: '-0.005em' }}>
              {(category || '').replace(/_/g, ' ')}
            </p>
            {fitConfidence != null && (
              <>
                <p className="text-[9px] tracking-[0.24em] uppercase mb-1" style={{ color: MUTED, fontWeight: 600 }}>AI Fit Confidence</p>
                <p className="text-[24px] leading-none"
                  style={{ fontFamily: 'Playfair Display, serif', color: GOLD, fontWeight: 600 }}>
                  {Math.round(fitConfidence)}%
                </p>
              </>
            )}
            {/* Beginner mode toggle */}
            <button
              data-testid="tg-beginner-toggle" onClick={toggleBeginner}
              className="mt-3 flex items-center gap-1.5 px-2.5 py-1.5 transition active:scale-[0.97]"
              style={{
                background: beginnerMode ? GOLD : '#FFF',
                color: beginnerMode ? INK : MUTED,
                border: `1px solid ${beginnerMode ? GOLD : LINE}`,
                fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                fontWeight: 600,
              }}>
              <BookOpen className="w-3 h-3" strokeWidth={1.6} />
              {beginnerMode ? 'Tailor Guidance ON' : 'Show Tailor Guidance'}
            </button>
          </div>
        </div>
        )}

        {/* Tailor Confirmation Recommended — softened from the old red panic state.
            We still surface fields where the customer didn't confirm, but the
            tone is luxury-warn (gold/ivory), not danger-red. */}
        {missing.length > 0 && (
          <div className="px-3 py-2.5 border-b" style={{ borderColor: LINE, background: 'rgba(212,175,55,0.06)' }}>
            <p className="text-[10px] tracking-[0.16em] uppercase mb-1.5 flex items-center gap-1.5"
              style={{ color: WARN, fontWeight: 700 }}>
              <Sparkles className="w-3 h-3" strokeWidth={1.8} />
              Tailor Confirmation Recommended ({missing.length})
            </p>
            <p className="text-[11px] mb-2" style={{ color: INK, lineHeight: 1.4 }}>
              AI has estimated these fields. Please confirm with the customer at first fitting.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {missing.map(f => (
                <span key={f.id} data-testid={`tg-missing-${f.id}`}
                  className="text-[10px] px-2 py-1"
                  style={{
                    background: '#FFF',
                    border: `1px solid rgba(212,175,55,0.55)`,
                    color: '#7A6420',
                    fontWeight: 600,
                  }}>
                  {f.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tailor recommendations — hidden in Expert Mode (dense workflow) */}
        {!expertMode && recommendations.length > 0 && (
          <div className="px-4 py-3 border-b" style={{ borderColor: LINE, background: '#FFF' }}>
            <p className="text-[10px] tracking-[0.20em] uppercase mb-2 flex items-center gap-1.5"
              style={{ color: GOLD, fontWeight: 700 }}>
              <Sparkles className="w-3 h-3" strokeWidth={1.8} />
              AI Tailor Recommendations
            </p>
            <div data-testid="tg-recommendations">
              {recommendations.map((r, i) => <RecommendationCard key={i} rec={r} />)}
            </div>
          </div>
        )}

        {/* Grouped fields */}
        {GROUP_ORDER.map(g => {
          const items = grouped[g];
          if (!items || items.length === 0) return null;
          const meta = GROUP_META[g];
          const isOpen = openGroups[g];
          return (
            <div key={g} data-testid={`tg-group-${g}`}>
              <button
                data-testid={`tg-group-toggle-${g}`}
                onClick={() => setOpenGroups({ ...openGroups, [g]: !isOpen })}
                className="w-full flex items-center justify-between px-3 py-2.5 border-b"
                style={{ borderColor: LINE, background: '#FCFAF6' }}>
                <span className="text-[10.5px] tracking-[0.16em] uppercase"
                  style={{ color: meta.tone, fontWeight: 600 }}>
                  {meta.label}
                  <span className="ml-2" style={{ color: MUTED, fontWeight: 400 }}>({items.length})</span>
                </span>
                {isOpen
                  ? <ChevronUp className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.6} />
                  : <ChevronDown className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.6} />}
              </button>
              {isOpen && (
                <div>
                  {items.map(f => (
                    <FieldRow key={f.id} field={f}
                      verified={!!verified[f.id]}
                      confidence={confidenceFor[f.id]}
                      expertMode={expertMode}
                      onToggleVerified={() => toggleVerified(f.id)}
                      onOpenHelp={() => setHelpField(f)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Alteration notes */}
        <div className="p-3 border-t" style={{ borderColor: LINE, background: SOFT }}>
          <p className="text-[10px] tracking-[0.16em] uppercase mb-1.5"
            style={{ color: MUTED, fontWeight: 600 }}>
            Alteration Notes (Tailor Only)
          </p>
          <textarea
            data-testid="tg-notes"
            value={notes}
            onChange={(e) => updateNotes(e.target.value)}
            placeholder="e.g., add 1 cm at side seams for letting out, double-line the placket…"
            className="w-full p-2.5 text-[11.5px] resize-y focus:outline-none"
            style={{
              border: `1px solid ${LINE}`, background: '#FFF',
              color: INK, lineHeight: 1.5, fontFamily: 'Outfit, sans-serif',
              minHeight: 70,
            }} />
          {onRequestClarification && (
            <button
              data-testid="tg-request-clarify"
              onClick={onRequestClarification}
              className="mt-2 flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.14em] uppercase"
              style={{
                background: '#FFF', border: `1px solid ${GOLD}`, color: WARN,
                fontWeight: 600,
              }}>
              <MessageCircleQuestion className="w-3 h-3" strokeWidth={1.6} />
              Request Clarification
            </button>
          )}
        </div>
      </div>

      {/* Help card modal */}
      {helpField && (
        <HelpCard field={helpField} beginner={beginnerMode}
          onClose={() => setHelpField(null)} />
      )}
    </>
  );
};

export default MeasurementGuide;
